import {useTranslation} from "react-i18next";
import {css} from "@emotion/react";
import {calculateRemSize} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text";
import {TFunction} from "i18next";

import {colorSets} from "../../../common/app/constants/colors";
import {IRoomsStructureItem} from "../mocks/singleOfferMock";

interface IRoomsStructureChartProps {
    data: IRoomsStructureItem[];
}

const ROOMS_COLORS = colorSets.alternate;
const ALL_COLOR = "#AAAAAA";

const getRoomColor = (rooms: number): string => {
    if (rooms === -1) {
        return ALL_COLOR;
    }
    return ROOMS_COLORS[(rooms - 1) % ROOMS_COLORS.length];
};

const getRoomLabel = (rooms: number, t: TFunction): string => {
    if (rooms === -1) {
        return t("offer_box.rooms_structure_all");
    }
    if (rooms === 1) {
        return t("offer_box.rooms_structure_studio");
    }
    return t("offer_box.rooms_structure_n", {count: rooms});
};

export const RoomsStructureChart = ({data}: IRoomsStructureChartProps) => {
    const {t} = useTranslation();

    const totalAll = data.reduce((acc, item) => acc + item.properties_count_total, 0);
    const forSaleAll = data.reduce((acc, item) => acc + item.properties_count_for_sale, 0);
    const rows: IRoomsStructureItem[] = [
        ...data,
        {rooms: -1, properties_count_total: totalAll, properties_count_for_sale: forSaleAll}
    ];

    return (
        <div css={chartGrid}>
            {rows.map((item) => {
                const forSalePct =
                    item.properties_count_total > 0
                        ? Math.round((item.properties_count_for_sale / item.properties_count_total) * 100)
                        : 0;
                const restPct = 100 - forSalePct;
                const color = getRoomColor(item.rooms);
                const isAllRow = item.rooms === -1;

                return (
                    <div key={item.rooms} css={[rowWrapper, isAllRow && boldRow]}>
                        <Text variant="info_txt_1" css={roomLabel}>
                            {getRoomLabel(item.rooms, t)}
                        </Text>
                        <div css={barContainer}>
                            <div css={pctRow}>
                                {forSalePct > 0 && <span css={pctSpan(forSalePct, "left")}>{forSalePct}%</span>}
                                {restPct > 0 && <span css={pctSpan(restPct, "right")}>{restPct}%</span>}
                            </div>
                            <div css={barTrack}>
                                <div css={barSegment(forSalePct, color)} />
                                <div css={barSegment(restPct, `${color}40`)} />
                            </div>
                        </div>
                        <Text variant="info_txt_1" css={countLabel}>
                            {item.properties_count_for_sale}/{item.properties_count_total}
                        </Text>
                    </div>
                );
            })}
        </div>
    );
};

const chartGrid = css`
    display: grid;
    grid-template-columns: max-content 1fr max-content;
    column-gap: ${calculateRemSize(1)};
    row-gap: ${calculateRemSize(1)};
`;

const rowWrapper = css`
    display: contents;
`;

const boldRow = css`
    * {
        font-weight: 700;
    }
`;

const roomLabel = css`
    color: #555;
    white-space: nowrap;
    padding-top: ${calculateRemSize(2)};
`;

const barContainer = css`
    min-width: 0;
`;

const pctRow = css`
    display: flex;
    width: 100%;
    font-size: ${calculateRemSize(1)};
    line-height: 1.4;
    color: #444;
`;

const pctSpan = (pct: number, align: "left" | "right") => css`
    width: ${pct}%;
    text-align: ${align === "left" ? "center" : "center"};
    overflow: hidden;
    white-space: nowrap;
    font-size: ${calculateRemSize(1)};
`;

const barTrack = css`
    display: flex;
    width: 100%;
    height: ${calculateRemSize(1.5)};
    border-radius: ${calculateRemSize(1)};
    overflow: hidden;
`;

const barSegment = (pct: number, color: string) => css`
    width: ${pct}%;
    background-color: ${color};
    transition: width 0.3s ease;
`;

const countLabel = css`
    text-align: right;
    color: #333;
    white-space: nowrap;
    padding-top: ${calculateRemSize(2)};
`;
